/**
 * 通用时间格式处理工具
 * 同时兼容Vue2和Vue3
 * 将API返回的RFC3339格式时间转换为 Y-m-d H:i:s 格式
 */

/**
 * 格式化Unix时间戳
 * @param {string|number} unixTimeStr - Unix时间戳（秒级或毫秒级）
 * @param {string} format - 格式字符串，默认 'Y-m-d H:i:s'
 * @returns {string} 格式化后的时间字符串
 */
export function formatUnixTime(unixTimeStr, format = 'Y-m-d H:i:s') {
	if (!unixTimeStr) {
		return ''
	}

	// 转换为数字
	let timestamp = typeof unixTimeStr === 'string' ? parseFloat(unixTimeStr) : unixTimeStr

	if (isNaN(timestamp) || timestamp <= 0) {
		return ''
	}

	// 判断是秒级还是毫秒级时间戳（秒级通常是10位，毫秒级是13位）
	if (timestamp < 10000000000) {
		// 秒级时间戳，转换为毫秒
		timestamp = timestamp * 1000
	}

	const date = new Date(timestamp)

	// 检查日期是否有效
	if (isNaN(date.getTime())) {
		return ''
	}

	// 格式化函数
	const pad = (num, len = 2) => String(num).padStart(len, '0')

	const year = date.getFullYear()
	const month = pad(date.getMonth() + 1)
	const day = pad(date.getDate())
	const hours = pad(date.getHours())
	const minutes = pad(date.getMinutes())
	const seconds = pad(date.getSeconds())

	// 替换格式字符串
	return format
		.replace(/Y/g, year)
		.replace(/m/g, month)
		.replace(/d/g, day)
		.replace(/H/g, hours)
		.replace(/i/g, minutes)
		.replace(/s/g, seconds)
}
/**
 * 格式化时间字符串
 * @param {string} timeStr - 时间字符串
 * @returns {string} 格式化后的时间字符串
 */
export function formatTime(timeStr) {
	if (!timeStr || typeof timeStr !== 'string') {
		return timeStr || ''
	}

	// 尝试解析不同的时间格式
	const timeFormats = [
		// RFC3339 格式: 2025-10-12T23:13:56+08:00
		/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})([+-]\d{2}:\d{2}|Z)$/,
		// RFC3339Nano 格式: 2025-10-12T23:13:56.123456789+08:00
		/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})\.\d+([+-]\d{2}:\d{2}|Z)$/,
		// 简化格式: 2025-10-12T23:13:56Z
		/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})Z$/
	]

	for (const pattern of timeFormats) {
		const match = timeStr.match(pattern)
		if (match) {
			// 提取日期和时间部分
			const date = match[1] // 2025-10-12
			const time = match[2] // 23:13:56
			return `${date} ${time}`
		}
	}

	// 如果已经是 Y-m-d H:i:s 格式，直接返回
	if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(timeStr)) {
		return timeStr
	}

	// 如果无法识别，返回原始字符串
	return timeStr
}

/**
 * 格式化日期（只显示日期部分）
 * @param {string} timeStr - 时间字符串
 * @returns {string} 格式化后的日期字符串
 */
export function formatDate(timeStr) {
	if (!timeStr || typeof timeStr !== 'string') {
		return timeStr || ''
	}

	const formatted = formatTime(timeStr)
	// 提取日期部分
	const dateMatch = formatted.match(/^(\d{4}-\d{2}-\d{2})/)
	return dateMatch ? dateMatch[1] : formatted
}

/**
 * 格式化时间（只显示时间部分）
 * @param {string} timeStr - 时间字符串
 * @returns {string} 格式化后的时间字符串
 */
export function formatTimeOnly(timeStr) {
	if (!timeStr || typeof timeStr !== 'string') {
		return timeStr || ''
	}

	const formatted = formatTime(timeStr)
	// 提取时间部分
	const timeMatch = formatted.match(/(\d{2}:\d{2}:\d{2})$/)
	return timeMatch ? timeMatch[1] : formatted
}

/**
 * 相对时间格式化（如：2小时前、3天前）
 * @param {string} timeStr - 时间字符串
 * @returns {string} 相对时间字符串
 */
export function formatRelativeTime(timeStr) {
	if (!timeStr || typeof timeStr !== 'string') {
		return timeStr || ''
	}

	try {
		const date = new Date(timeStr)
		const now = new Date()
		const diff = now - date

		const seconds = Math.floor(diff / 1000)
		const minutes = Math.floor(seconds / 60)
		const hours = Math.floor(minutes / 60)
		const days = Math.floor(hours / 24)

		if (days > 0) {
			return `${days}天前`
		} else if (hours > 0) {
			return `${hours}小时前`
		} else if (minutes > 0) {
			return `${minutes}分钟前`
		} else {
			return '刚刚'
		}
	} catch (error) {
		return formatTime(timeStr)
	}
}

/**
 * 检测Vue版本
 * @returns {string} 'vue2' | 'vue3' | 'unknown'
 */
function detectVueVersion() {
	// 检查全局Vue对象
	if (typeof window !== 'undefined' && window.Vue) {
		return window.Vue.version && window.Vue.version.startsWith('3') ? 'vue3' : 'vue2'
	}

	// 检查模块导入的Vue
	try {
		const Vue = require('vue')
		return Vue.version && Vue.version.startsWith('3') ? 'vue3' : 'vue2'
	} catch (e) {
		// 如果无法检测，返回unknown
		return 'unknown'
	}
}

/**
 * 创建Vue2兼容的Composable
 * 在Vue2中返回methods对象
 */
export function useTimeFormat() {
	const version = detectVueVersion()

	if (version === 'vue2') {
		// Vue2: 返回methods对象
		return {
			formatTimeString: formatTime,
			formatDateString: formatDate,
			formatTimeOnlyString: formatTimeOnly,
			formatRelativeTimeString: formatRelativeTime,
			formatUnixTimeString: formatUnixTime,
			// Vue2兼容：将函数作为methods返回
			formatTime,
			formatDate,
			formatTimeOnly,
			formatRelativeTime,
			formatUnixTime
		}
	} else {
		// Vue3: 返回Composable函数
		return {
			formatTimeString: formatTime,
			formatDateString: formatDate,
			formatTimeOnlyString: formatTimeOnly,
			formatRelativeTimeString: formatRelativeTime,
			formatUnixTimeString: formatUnixTime
		}
	}
}

// 默认导出
export default {
	formatTime,
	formatDate,
	formatTimeOnly,
	formatRelativeTime,
	formatUnixTime,
	useTimeFormat
}
